import { action, computed, observable, runInAction } from 'mobx'
import type {
  CreateMemberFn,
  GetCurrentUserWorkspaceMembershipsFn,
  GetWorkspaceRolesFn,
  GetWorkspaceSkillsFn,
  GetWorkspaceTeamRolesFn,
  GetWorkspaceTeamsFn,
  ListRolesFn,
  ListSkillsFn,
  ListTeamRolesFn,
  ListTeamsFn,
} from '@/features/iam'
import { task } from 'mobx-task'
import { func, validationContext } from 'validx'
import type {
  AlertErrorHandlerFactory,
  AlertFactory,
  AlertRetryWrapperFactory,
} from '@/features/shell'
import { type Role } from '@/features/iam/roles/Role'
import { type SearchableUser } from '@/features/iam/users/SearchableUser'
import { type Skill } from '@/features/iam/skills/Skill'
import { type Team } from '@/features/iam/teams/Team'
import { type TeamRole } from '@/features/iam/teams/TeamRole'
import { type Workspace } from '@/features/iam/workspaces/Workspace'
import { ModalViewModel } from '@/infra/ModalViewModel'
import { Id } from '@/infra/id'
import type { ActivationLifecycle } from '@/infra/activation'
import { LifecycleTracker } from '@/infra/lifecycle-tracker'
import { toStream } from '@/utils/mobx-util'
import { withAbortSignal } from '@/utils/rx-util'
import { catchError, distinctUntilChanged, EMPTY, switchMap } from 'rxjs'

export class AddUserToWorkspaceDialogViewModel implements ActivationLifecycle {
  private readonly _lifecycle = new LifecycleTracker()

  readonly modal = new ModalViewModel({ onClose: () => this.reset() })

  /**
   * The currently selected workspace.
   */
  @observable
  accessor workspace: Workspace | null = null

  /**
   * The currently selected workspace role.
   */
  @observable
  accessor workspaceRole: Role | null = null

  /**
   * The currently selected skills.
   */
  @observable
  accessor skills: Skill[] = []

  /**
   * The currently selected team.
   */
  @observable
  accessor team: Team | null = null

  /**
   * The currently selected team role.
   */
  @observable
  accessor teamRole: TeamRole | null = null

  /**
   * The list of workspace roles.
   */
  @observable
  accessor workspaceRoles: Role[] = []

  /**
   * The list of available skills.
   */
  @observable
  accessor availableSkills: Skill[] = []

  /**
   * The list of teams.
   */
  @observable
  accessor teams: Team[] = []

  /**
   * The list of team roles.
   */
  @observable
  accessor teamRoles: TeamRole[] = []

  /**
   * Validation rules for the add user to workspace form.
   */
  readonly validation = validationContext(this, {
    workspace: [
      func(() => this.workspace !== null, 'You must select a workspace.'),
    ],
    workspaceRole: [
      func(
        () => this.workspaceRole !== null,
        'You must select a workspace role.',
      ),
    ],
    teamRole: [
      func(
        () => this.team === null || this.teamRole !== null,
        'If you select a team, you must also select a team role.',
      ),
    ],
  })

  private readonly _options$ = toStream(() => this.workspace, true).pipe(
    distinctUntilChanged(),
    switchMap((workspace) =>
      workspace
        ? withAbortSignal((signal) =>
            this._fetchOptions(workspace, signal),
          ).pipe(
            // The retry alert has reported the failure.
            catchError(() => EMPTY),
          )
        : EMPTY,
    ),
  )

  constructor(
    private readonly _user: () => SearchableUser,
    private readonly _onAdded: () => Promise<void>,
    private readonly _listRoles: ListRolesFn,
    private readonly _getWorkspaceRoles: GetWorkspaceRolesFn,
    private readonly _listSkills: ListSkillsFn,
    private readonly _getWorkspaceSkills: GetWorkspaceSkillsFn,
    private readonly _listTeams: ListTeamsFn,
    private readonly _getWorkspaceTeams: GetWorkspaceTeamsFn,
    private readonly _listTeamRoles: ListTeamRolesFn,
    private readonly _getWorkspaceTeamRoles: GetWorkspaceTeamRolesFn,
    private readonly _createMember: CreateMemberFn,
    private readonly _getCurrentUserWorkspaceMemberships: GetCurrentUserWorkspaceMembershipsFn,
    private readonly _newAlert: AlertFactory,
    private readonly _createAlertErrorHandler: AlertErrorHandlerFactory,
    private readonly _createAlertRetryWrapper: AlertRetryWrapperFactory,
  ) {
    this._lifecycle.manage(this._options$)
  }

  /** @inheritDoc */
  activate() {
    this._lifecycle.activate()
  }

  /** @inheritDoc */
  deactivate() {
    this._lifecycle.deactivate()
  }

  /**
   * The currently selected user.
   */
  @computed
  get user() {
    return this._user()
  }

  /**
   * The list of workspaces the current user (i.e. the admin) may create members in.
   */
  @computed
  get currentUserWorkspaces() {
    return this._getCurrentUserWorkspaceMemberships()
      .filter(
        (mb) =>
          mb.member.permissions.adminUserModify &&
          mb.member.permissions.adminUserRolesModify,
      )
      .map((mb) => mb.workspace)
  }

  /**
   * The list of workspaces the current user (i.e. the admin) may create team members in.
   */
  @computed
  get currentUserTeamWorkspaces() {
    return this._getCurrentUserWorkspaceMemberships()
      .filter((mb) => mb.member.permissions.adminManageTeamMember)
      .map((mb) => mb.workspace)
  }

  /**
   * The list of workspaces the currently selected user (i.e. the one being viewed)
   * is already a member of.
   */
  @computed
  get selectedUserWorkspaces() {
    return this.user.memberships.map((mb) => mb.workspace)
  }

  /**
   * The list of workspaces that the current user may create members in
   * AND that the currently selected user is NOT already a member of.
   */
  @computed
  get availableWorkspaces() {
    return this.currentUserWorkspaces.filter(
      (cuws) =>
        !this.selectedUserWorkspaces.some((suws) => suws.id === cuws.id),
    )
  }

  /**
   * Set the currently selected workspace.
   */
  @action.bound
  setWorkspace(workspace?: Workspace | null) {
    if (workspace === this.workspace) {
      return
    }

    this.workspace = workspace ?? null
    this.clearSelections()
  }

  /**
   * Set the currently selected workspace role.
   */
  @action.bound
  setWorkspaceRole(role?: Role | null) {
    this.workspaceRole = role ?? null
  }

  /**
   * Set the currently selected skills.
   */
  @action.bound
  setSkills(skills: Skill[]) {
    this.skills = skills
  }

  /**
   * Set the currently selected team.
   */
  @action.bound
  setTeam(team?: Team | null) {
    this.team = team ?? null
  }

  /**
   * Set the currently selected team role.
   */
  @action.bound
  setTeamRole(role?: TeamRole | null) {
    this.teamRole = role ?? null
  }

  /**
   * Clear relevant selections when a new workspace is selected.
   */
  @action.bound
  clearSelections() {
    this.workspaceRole = null
    this.skills = []
    this.team = null
    this.teamRole = null
  }

  /**
   * Reset the view model to its default state.
   */
  @action.bound
  reset() {
    this.workspace = null
    this.clearSelections()
    this.workspaceRoles = []
    this.availableSkills = []
    this.teams = []
    this.teamRoles = []
    this.validation.reset()
  }

  /**
   * Add the currently selected user to the selected workspace
   */
  submit = task.resolved(async () => {
    const { workspace, workspaceRole } = this
    if (
      !this.validation.reset().validate().isValid ||
      !workspace ||
      !workspaceRole
    ) {
      return
    }

    const member = await this._createMember({
      userId: Id.assert(this.user.publicId),
      workspaceId: workspace.id,
      givenName: this.user.givenName,
      familyName: this.user.familyName,
      email: this.user.email,
      phone: this.user.phone,
      roleId: Id.assert(workspaceRole.id),
      skillIds: this.skills.map((s) => s.id),
      teamId: this.team?.id,
      teamRoleId: this.teamRole?.id,
      source: 'Local',
    }).catch(
      this._createAlertErrorHandler('Adding the user to the workspace failed'),
    )

    this._onAdded().catch(() => undefined)

    this._newAlert()
      .withState('success')
      .saying(
        'Success!',
        `Added ${member.displayName} to ${this.workspace?.name} as ${this.workspaceRole?.name}.`,
      )
      .autoDismiss(2500)
      .show()

    this.modal.close()
  })

  private async _fetchOptions(workspace: Workspace, signal: AbortSignal) {
    const workspaceId = workspace.id
    const withRetry = this._createAlertRetryWrapper(
      'Loading workspace options failed',
      signal,
    )
    await withRetry(async () => {
      await this._listRoles({ workspaceId }, signal)
      await this._listSkills({ workspaceId }, signal)
      await this._listTeams({ workspaceId }, signal)
      await this._listTeamRoles({ workspaceId }, signal)
    })
    signal.throwIfAborted()

    runInAction(() => {
      this.workspaceRoles = this._getWorkspaceRoles(workspaceId)
      this.availableSkills = this._getWorkspaceSkills(workspaceId)
      this.teams = this._getWorkspaceTeams(workspaceId)
      this.teamRoles = this._getWorkspaceTeamRoles(workspaceId)
    })
  }
}
